import { useEffect, useRef, useState } from 'react'
import { GetScreenState as BLGetScreenState } from './../../utilities/BL/Admin/Overlays/NoisemeterV2'
import { motion } from 'framer-motion'
import Confetti from 'react-confetti'


const NoisemeterV2 = ({ data, isActiveWomensMode }) => { 
    
    // --- ESTADOS ---
    const [gameState, setGameState] = useState('WAITING'); 
    const [zones, setZones] = useState([]);
    const [winner, setWinner] = useState(null);
    const [displayedScores, setDisplayedScores] = useState({});

    const targetScoresRef = useRef({});
    const pollingRef = useRef(null);
    const { innerWidth: width, innerHeight: height } = window;

    // --------------------------------------------------------------------------------
    // LÓGICA DE DATOS
    // --------------------------------------------------------------------------------
    useEffect(() => {
        
        const updateScreen = (incoming) => {
            if (!incoming) return;

            // 1. NORMALIZACIÓN DE DATOS
            const rawStatus = incoming.status !== undefined ? incoming.status : incoming.Status;
            const rawWinner = incoming.winnerZone || incoming.WinnerZone;
            
            const rawZones = incoming.zones || incoming.Zones;
            const rawActiveList = incoming.activeZonesList || incoming.ActiveZonesList;
            
            const hasDates = incoming.startAt || incoming.StartAt;

            // 2. ACTUALIZACIÓN DE BARRAS
            if (rawZones && Array.isArray(rawZones) && rawZones.length > 0) {
                const cleanZones = rawZones.map(z => ({
                    zone: z.zone || z.Zone,
                    progressPercent: z.progressPercent !== undefined ? z.progressPercent : z.ProgressPercent,
                    currentIntensity: z.currentIntensity !== undefined ? z.currentIntensity : (z.CurrentIntensity || 0),
                    score: z.score !== undefined ? z.score : (z.Score || 0)
                }));
                setZones(cleanZones);
            } 
            else if (rawActiveList && Array.isArray(rawActiveList) && rawActiveList.length > 0) {
                const initialZones = rawActiveList.map(name => ({
                    zone: name,
                    progressPercent: 0,
                    currentIntensity: 0,
                    score: 0
                }));
                setZones(initialZones);
            }

            // 3. DETERMINAR ESTADO
            if (rawWinner && rawWinner !== 'none' && rawWinner !== 'tie' && rawWinner !== '') {
                setWinner(rawWinner);
                setGameState('FINISHED');
                return; 
            }
            if (rawWinner === 'tie') {
                setWinner('tie');
                setGameState('FINISHED');
                return;
            }
            if (rawStatus === 2 || rawStatus === 'Finished') {
                setGameState('FINISHED');
                return;
            }

            const isRunningExplicit = rawStatus === 1 || rawStatus === 'Running';
            const isImplicitStart = rawStatus === undefined && hasDates; 

            if (isRunningExplicit || isImplicitStart) {
                setGameState('RUNNING');
                setWinner(null); 
                return;
            }

            setGameState('WAITING');
            setWinner(null);
            setDisplayedScores({});
            targetScoresRef.current = {};
        };

        if (data) {
            console.log("[NoisemeterV2 Screen] Data inicial recibida por props:", data);
            updateScreen(data);
        }

        console.log("[NoisemeterV2 Screen] Inicializando useEffect. Tipo de BLGetScreenState:", typeof BLGetScreenState);

        const fetchState = async () => {
            if (typeof BLGetScreenState !== 'undefined') {
                try {
                    const res = await BLGetScreenState();
                    console.log("[NoisemeterV2 Screen] Polling resultado:", res);
                    if (res?.success && res?.data) {
                        updateScreen(res.data);
                    } else {
                        console.warn("[NoisemeterV2 Screen] Polling falló o data vacía:", res);
                    }
                } catch (e) {
                    console.error("[NoisemeterV2 Screen] Excepción en fetchState:", e);
                }
            } else {
                console.error("[NoisemeterV2 Screen] BLGetScreenState es UNDEFINED en el scope!");
            }
        };

        if (!pollingRef.current) {
            console.log("[NoisemeterV2 Screen] Iniciando setInterval de polling cada 500ms");
            pollingRef.current = setInterval(fetchState, 500);
        }

        return () => {
            if (pollingRef.current) {
                console.log("[NoisemeterV2 Screen] Limpiando setInterval de polling");
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };

    }, [data]); 

    // Sincronizar los targets de puntuación
    useEffect(() => {
        if (zones && zones.length > 0) {
            zones.forEach(z => {
                targetScoresRef.current[z.zone] = z.score || 0;
            });
        }
    }, [zones]);

    // Loop de animación para interpolación fluida de contadores (Puntos acumulados)
    useEffect(() => {
        let animationFrameId;
        const step = () => {
            setDisplayedScores(prev => {
                const next = { ...prev };
                let changed = false;
                Object.keys(targetScoresRef.current).forEach(zone => {
                    const target = targetScoresRef.current[zone];
                    const current = prev[zone] || 0;
                    
                    if (current < target) {
                        const diff = target - current;
                        // Interpolación exponencial: 8% del camino restante por cuadro + mínimo de 0.05
                        const increment = Math.max(0.05, diff * 0.08);
                        next[zone] = Math.min(target, current + increment);
                        changed = true;
                    } else if (current > target) {
                        next[zone] = target; // Si baja, reseteamos directo
                        changed = true;
                    }
                });
                if (changed) return next;
                return prev;
            });
            animationFrameId = requestAnimationFrame(step);
        };
        animationFrameId = requestAnimationFrame(step);
        return () => cancelAnimationFrame(animationFrameId);
    }, []);

    // Helper para obtener estilo visual e ícono por zona
    const getZoneStyle = (zoneName) => {
        const n = (zoneName || '').toLowerCase();
        if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
        if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
        // if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
        if (n.includes('san luis')) return { colorClass: 'blue', hex: '#0055a5' };
        // if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
        if (n.includes('corona')) return { colorClass: 'red', hex: '#cb0909' };
        if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
        if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
        if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
        // if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
        if (n.includes('atleti')) return { colorClass: 'gray', hex: '#666666' };
        if (n.includes('laboratorios tequis')) return { colorClass: 'gray', hex: '#666666' };
        if (n.includes('seguros el potosi')) return { colorClass: 'gray', hex: '#666666' };
        if (n.includes('palcos')) return { colorClass: 'gray', hex: '#666666' };

        //Temporales
        if (n.includes('lucho')) return { colorClass: 'gray', hex: '#cb0909' };
        if (n.includes('tunita')) return { colorClass: 'gray', hex: '#004d98' };
        return { colorClass: 'red', hex: '#dc3545' };
    };

    const renderScoreCounter = (zoneName) => {
        const score = displayedScores[zoneName] || 0;
        const [integers, decimals] = score.toFixed(2).split('.');
        
        return (
            <div className="noisemeter-screen__points">
                <span className="noisemeter-screen__points-label">PUNTOS</span>
                <span className="noisemeter-screen__points-integers">{integers}</span>
                <span className="noisemeter-screen__points-decimals">.{decimals}</span>
                {/* <span className="noisemeter-screen__points-label"> pts.</span> */}
            </div>
        );
    };

    const renderVersusMode = () => {
        if (zones.length < 2) return null;
        const zoneA = zones[0];
        const zoneB = zones[1];
        const styleA = getZoneStyle(zoneA.zone);
        const styleB = getZoneStyle(zoneB.zone);

        return (
            <div className="noisemeter-screen__versus">
                {/* Zona Izquierda */}
                <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
                    {renderScoreCounter(zoneA.zone)}

                    <p className='mt-4 mb-2 fs-5'>INTENSIDAD</p>
                    <div className="noisemeter-screen__bar-track">
                        <div 
                            className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} 
                            style={{ 
                                height: `${zoneA.currentIntensity}%`, 
                                background: styleA.hex,
                                transition: 'height 0.15s ease-out'
                            }}
                        >
                            <span className="noisemeter-screen__score">{Math.floor(zoneA.currentIntensity)}% VOL</span>
                        </div>
                    </div>
                    <p className="noisemeter-screen__label mt-3">{zoneA.zone.replace(/Zona\s/i, '')}</p>
                </div>
                
                <div className="noisemeter-screen__vs-badge">VS</div>
                
                {/* Zona Derecha */}
                <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
                    {renderScoreCounter(zoneB.zone)}

                    <p className='mt-4 mb-2 fs-5'>INTENSIDAD</p>
                    <div className="noisemeter-screen__bar-track">
                        <div 
                            className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} 
                            style={{ 
                                height: `${zoneB.currentIntensity}%`, 
                                background: styleB.hex,
                                transition: 'height 0.15s ease-out'
                            }}
                        >
                            <span className="noisemeter-screen__score">{Math.floor(zoneB.currentIntensity)}% VOL</span>
                        </div>
                    </div>
                    <p className="noisemeter-screen__label mt-3">{zoneB.zone.replace(/Zona\s/i, '')}</p>
                </div>
            </div>
        );
    };

    const renderMultiMode = () => {
        return (
            <div className="noisemeter-screen__multi">
                {zones.map((z) => {
                    const style = getZoneStyle(z.zone);
                    const labelFontSize = zones.length > 5 ? '1rem' : '1.5rem';
                    const labelText = z.zone.replace(/Zona\s/i, ''); 
                    
                    return (
                        <div key={z.zone} className="noisemeter-screen__multi-column">
                            {renderScoreCounter(z.zone)}
                            
                            <p className='mt-4 mb-0'>INTENSIDAD</p>
                            <div className="noisemeter-screen__multi-track" style={{ marginTop: '10px' }}>
                                <div 
                                    className="noisemeter-screen__multi-fill" 
                                    style={{ 
                                        height: `${z.currentIntensity}%`, 
                                        background: style.hex, 
                                        boxShadow: `0 0 20px ${style.hex}60`,
                                        transition: 'height 0.15s ease-out'
                                    }}
                                ></div>
                            </div>
                            <p className="noisemeter-screen__multi-column__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="noisemeter-screen">
            <div className="noisemeter-screen__bg">
                {isActiveWomensMode ? (
                    <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
                ) : (
                    <img src='/img/page/backgrounds/background.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
                )}
            </div>

            <div className='Noisemeter__Screen__Box'>
                <div className="noisemeter-screen__header">
                    <img src="/img/page/adsl-five.png" className="Noisemeter__Screen__Logo" alt="Logo" style={{ maxWidth: '600px' }} />
                </div>

                <div className="noisemeter-screen__content">
                    {gameState === 'WAITING' && (
                        <div className="FrontPageNoisemeter__Screen__Container m-auto text-center">
                            <p className="FrontPageNoisemeter__Screen__Container__Title" style={{ fontFamily: '"GFS Didot", serif', fontSize: '6.5rem', fontWeight: 'bold', textShadow: '2px 2px 8px rgba(0,0,0,0.5)', margin: '0' }}>
                                RUIDÓMETRO
                            </p>
                        </div>
                    )}

                    {gameState === 'RUNNING' && (
                        <>
                           {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
                        </>
                    )}

                    {gameState === 'FINISHED' && (
                        <>
                            <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
                                {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
                            </div>

                            {winner && (
                                <div className="Results__FullScreenOverlay">
                                    <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
                                        <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                        
                                        {winner === 'tie' ? 
                                            <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p> :
                                            <>
                                                <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
                                                <p className="Results__FullScreenOverlay__OneWinner">
                                                    {/* {winner.toUpperCase() === 'CANELS' ? 'LUCHO' : winner.toUpperCase() === 'TECATE' ? 'TUNITA' : winner.toUpperCase() === 'AZUL' ? 'LUCHO' : winner.toUpperCase()} */}
                                                    {winner.toUpperCase()}
                                                </p>
                                            </>
                                        }
                                    </motion.div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Interaktia | 2026</footer>
        </div>
    );
};

export { NoisemeterV2 };
